import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import CryptoJS from 'crypto-js';
import { setOrgId, setUserId, setUserOrgs } from './auth';
import { getIsLocalDevelopment, getIsNonProdEnv } from '../util-helpers/common';
import { AddInStorage } from '../storage/AddInStorage';
import { nrNoticeError } from '../util-helpers/newrelic';
import { showNotification } from '../util-helpers/notification';
import { canInitiateAuthRefresh, resetAuthRefreshState } from '../util-helpers/authErrorManager';
import { openDialog } from '../util-helpers/officeDialog';

// Logging function
const log = (type: string, data: any, backgroundColor = 'blue') => {
    if (getIsLocalDevelopment() || getIsNonProdEnv()) {
        console.log(`%c ${type} HTTP:`, `background-color: ${backgroundColor}; color: #ffffff; padding: 20px`, data);
    }
};

// Function to set up interceptors
const setupInterceptors = (instance: any, type: string) => {
    instance.interceptors.request.use(
        (config: any) => {
            log(`${type} Request`, { URL: config.url, payload: config.data }, 'blue');
            return config;
        },
        (error: any) => {
            nrNoticeError(error, {
                type: `${type}RequestError`,
                currentPage: window.location.href
            });
            return Promise.reject(error);
        }
    );

    instance.interceptors.response.use(
        (response: any) => {
            log(`${type} Response`, { Status: response.status, Body: response.data }, 'green');
            return response;
        },
        (error: any) => {
            const errorResponse = error.response || {};
            const status = errorResponse.status;
            const errorData = {
                URL: errorResponse.config?.url,
                Status: status,
                Message: error.message,
                currentPage: window.location.href
            };

            nrNoticeError(error, {
                type: `${type}ResponseError`,
                ...errorData
            });

            return Promise.reject(error);
        }
    );
};

const api = axios.create({});
setupInterceptors(api, 'API');

// ################ OAUTH ########################################
export type EnvironmentType = "LOCAL" | "NONPROD" | "US" | "CA";

interface FVIDSettings {
    STS_AUTHORITY: string;
    CLIENT_ID: string;
    CLIENT_ROOT: string;
    API_BASE: string;
}

interface EnvironmentSettings {
    LOCAL: FVIDSettings;
    NONPROD: FVIDSettings;
    US: FVIDSettings;
    CA: FVIDSettings;
    [key: string]: FVIDSettings;
}

interface User {
    expired: boolean;
    profile: any;
}

export const ENV_SETTNGS: EnvironmentSettings = {
    LOCAL: {
        STS_AUTHORITY: "https://identity-nonprod-public.filevinedev.com",
        CLIENT_ID: "31ec7f87-0922-4ee5-b928-888f73666c37",
        CLIENT_ROOT: "https://localhost:3003",
        API_BASE: "https://fru.filevinedev.com/v2"
    },
    NONPROD: {
        STS_AUTHORITY: "https://identity-nonprod-public.filevinedev.com",
        CLIENT_ID: "31ec7f87-0922-4ee5-b928-888f73666c37",
        CLIENT_ROOT: "https://outlook.filevinedev.com",
        API_BASE: "https://api.filevinedev.com/fv-app/v2"
    },
    US: {
        STS_AUTHORITY: "https://identity.filevine.com",
        CLIENT_ID: "9afe7234-bd2d-421c-9ab7-43bdc413ab84",
        CLIENT_ROOT: "https://outlook.filevine.com",
        API_BASE: "https://api.filevineapp.com/fv-app/v2"
    },
    CA: {
        STS_AUTHORITY: "https://identity.filevine.ca",
        CLIENT_ID: "69715aa3-908d-41d8-87a8-2a240cabde7f",
        CLIENT_ROOT: "https://outlook.filevine.com",
        API_BASE: "https://api.filevineapp.ca/fv-app/v2"
    }
};

export const attemptTokenRefresh = async (): Promise<boolean> => {
    try {
        const refreshToken = AddInStorage.getItem("refresh_token");

        if (!refreshToken) {
            return false;
        }

        const newTokens = await FVID.refreshToken(refreshToken);

        AddInStorage.setItem("access_token", newTokens.access_token);
        AddInStorage.setItem("refresh_token", newTokens.refresh_token);

        await generateHeaders(true);

        return true;
    } catch (error) {
        console.error("Error refreshing token:", error);
        return false;
    }
};

export const refreshAccess = async (autoRedirect = true, errorMessage?: string): Promise<void> => {
    // Check if we can initiate a refresh (prevents multiple simultaneous refresh attempts)
    if (!canInitiateAuthRefresh()) return;

    try {
        const notificationMessage = errorMessage || 'Your session has expired';
        showNotification(notificationMessage, false, false);

        // First, try to refresh the token
        const tokenRefreshed = await attemptTokenRefresh();

        if (tokenRefreshed) {
            resetAuthRefreshState();
            showNotification('Your session has been refreshed. Reloading page...', false, false);

            // Wait a moment for the notification to be visible
            setTimeout(() => {
                // Reload the current page to ensure all requests use the new token
                window.location.reload();
            }, 1500);

            return;
        }

        const environment = getEnvironmentSuffix();

        // landingPage handles automatically opening login dialog to avoid another user action
        localStorage.setItem('lastEnvironment', environment);
        localStorage.setItem('autoSelectEnvironment', 'true');

        showNotification('Your session has expired. Redirecting to login...', false, false);

        AddInStorage.clear();

        if (autoRedirect) {
            resetAuthRefreshState();

            window.location.assign('/');
        } else {
            resetAuthRefreshState();
        }
    } catch (error) {
        resetAuthRefreshState();
        // Fall back to redirect in case of any errors
        if (autoRedirect) {
            window.location.assign('/');
        }
    }
};

export const getEnvironmentSuffix = (): EnvironmentType => {
    const accessToken = AddInStorage.getItem("access_token");
    let environment: EnvironmentType = "LOCAL"; // Default to "LOCAL"

    if (accessToken !== null && window.location.hostname !== "localhost") {
        const tokenParts = accessToken.split('.');
        if (tokenParts.length !== 3) {
            throw new Error('Invalid token format');
        }
        const payload = JSON.parse(atob(tokenParts[1]));
        const issuer = payload.iss;

        environment = issuer === "https://identity.filevine.ca" ? "CA"
            : (issuer === "https://identity.filevine.com" ? "US" : "NONPROD");

        return environment;
    }

    const userDomain = localStorage.getItem('userDomain') || "";
    if (window.location.hostname === "localhost") {
        environment = "LOCAL";
    } else if (window.location.host === "outlook.filevinedev.com") {
        environment = "NONPROD";
    } else if ((!userDomain && window.location.host === "outlook.filevine.com") || userDomain === ".filevineapp.com") {
        environment = "US";
    } else if (userDomain === ".filevineapp.ca") {
        environment = "CA";
    }

    return environment;
};

class FVID {
    static user: User = {
        expired: false,
        profile: undefined
    };

    static authConfig: any = {
        response_type: 'code',
        scope: 'openid profile email phone tenant filevine.v2.api.* theme fv.api.gateway.access fv.auth.tenant.read offline_access',
    }

    static oAuthApi: AxiosInstance;

    static setAuthConfig = () => {
        const environment: EnvironmentType = getEnvironmentSuffix();

        FVID.authConfig.authority = ENV_SETTNGS[environment].STS_AUTHORITY || "";
        FVID.authConfig.client_id = ENV_SETTNGS[environment].CLIENT_ID || "";
        FVID.authConfig.redirect_uri = ENV_SETTNGS[environment].CLIENT_ROOT + "/callback";
        FVID.authConfig.post_logout_redirect_uri = ENV_SETTNGS[environment].CLIENT_ROOT + "/signout";

        FVID.oAuthApi = axios.create({
            baseURL: FVID.authConfig.authority,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        });
        setupInterceptors(FVID.oAuthApi, 'OAUTH');
    }

    static authorize = async () => {
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        localStorage.setItem('code_verifier', codeVerifier);

        const params = {
            client_id: FVID.authConfig.client_id,
            redirect_uri: FVID.authConfig.redirect_uri,
            response_type: FVID.authConfig.response_type,
            scope: FVID.authConfig.scope,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
        };
        const queryString = createQueryString(params);
        window.location.href = `${FVID.authConfig.authority}/connect/authorize?${queryString}`;
    };

    static exchangeCodeForToken = async (code: string) => {
        const codeVerifier = localStorage.getItem('code_verifier');
        if (!codeVerifier) {
            throw new Error('Code verifier not found');
        }
        localStorage.removeItem('code_verifier');

        FVID.setAuthConfig();

        const data = createQueryString({
            grant_type: 'authorization_code',
            client_id: FVID.authConfig.client_id,
            redirect_uri: FVID.authConfig.redirect_uri,
            code,
            code_verifier: codeVerifier,
        });

        const response = await FVID.oAuthApi.post('/connect/token', data);

        return response.data;
    };

    static refreshToken = async (refreshToken: string) => {
        const data = createQueryString({
            grant_type: 'refresh_token',
            client_id: FVID.authConfig.client_id,
            refresh_token: refreshToken,
        });

        const response = await FVID.oAuthApi.post('/connect/token', data);
        return response.data;
    };

    static getUserInfo = async (accessToken: string) => {
        const response = await FVID.oAuthApi.get('/connect/userinfo', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        FVID.user = response.data.Body;
        await generateHeaders(true);
        return response.data;
    };

    static signout = () => {
        const revokeTokensAndClearStorage = async () => {
            const refreshToken = AddInStorage.getItem("refresh_token");
            const accessToken = AddInStorage.getItem("access_token");

            if (refreshToken) {
                try {
                    await FVID.oAuthApi.post('/connect/revocation', createQueryString({
                        token: refreshToken,
                        token_type_hint: 'refresh_token',
                        client_id: FVID.authConfig.client_id
                    }));
                } catch (error) {
                    console.error("Error revoking refresh token:", error);
                }
            }

            if (accessToken) {
                try {
                    await FVID.oAuthApi.post('/connect/revocation', createQueryString({
                        token: accessToken,
                        token_type_hint: 'access_token',
                        client_id: FVID.authConfig.client_id
                    }));
                } catch (error) {
                    console.error("Error revoking access token:", error);
                }
            }

            AddInStorage.clear();
            window.location.assign("/");
        };

        try {
            openDialog({
                url: `${ENV_SETTNGS[getEnvironmentSuffix()].CLIENT_ROOT}/logout`,
                onMessage: revokeTokensAndClearStorage,
                onError: (error: any) => {
                    console.log('Sign out error:', error);
                    // Still clear the storage and redirect even if there is an error
                    revokeTokensAndClearStorage();
                }
            });
        } catch (error) {
            console.error("Error opening signout dialog:", error);
            // Still clear the storage and redirect even if there is an error
            revokeTokensAndClearStorage();
        }

    }

    static signoutDialog = (idToken: string) => {
        try {
            const postLogoutRedirectUri = FVID.authConfig.post_logout_redirect_uri;
            const signoutUrl = `${FVID.authConfig.authority}/connect/endsession?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
            window.location.href = signoutUrl;
        } catch (error) {
            console.error("Error during signout:", error);
            Office.context.ui.messageParent(JSON.stringify({ status: 'signout_error', data: JSON.stringify(error) }));
        }
    }

    static getFVIDAccessToken = async () => {
        const accessToken = AddInStorage.getItem("access_token");
        const refreshToken = AddInStorage.getItem("refresh_token");

        if (accessToken) {
            // Decode the access token to extract the expiration time
            const tokenParts = accessToken.split('.');
            if (tokenParts.length !== 3) {
                console.error("Invalid token format.");
                refreshAccess(true, 'Authentication error - Invalid token format');
                return null;
            }

            const payload = JSON.parse(atob(tokenParts[1])); // Decode the payload (base64)
            const now = Math.floor(Date.now() / 1000); // current time in seconds

            // Check if the token is expired
            if (now >= payload.exp && refreshToken) {
                try {
                    const newAccessToken = await FVID.refreshToken(refreshToken);

                    AddInStorage.setItem("access_token", newAccessToken.access_token);
                    AddInStorage.setItem("refresh_token", newAccessToken.refresh_token);

                    return newAccessToken.access_token;
                } catch (error) {
                    console.error("Error refreshing token...", error);
                    refreshAccess(true, 'Authentication error - Unable to refresh your session');
                    return null;
                }
            }
        } else {
            refreshAccess(true, 'Authentication error - Missing access token');
            return null;
        }

        return accessToken;
    };
}

FVID.setAuthConfig();

// ################# HELPER FUNCTIONS ################################
export const generateHeaders = async (includeUserIDandOrg = false):
    Promise<AxiosRequestHeaders> => {
    const accessToken = await FVID.getFVIDAccessToken();
    if (!accessToken) {
        return {};
    }

    const headers: AxiosRequestHeaders = {
        Authorization: `Bearer ${accessToken}`,
        "x-fv-clientip": await getClientIP()
    };

    if (includeUserIDandOrg) {
        const orgsAndUserID = await getUserOrgsAndUserID();
        if (orgsAndUserID && orgsAndUserID.orgs
            && orgsAndUserID.orgs.length > 0
            && orgsAndUserID.ID > 0) {
            setUserId(orgsAndUserID.ID);
            setUserOrgs(orgsAndUserID.orgs);
            setOrgId(orgsAndUserID.orgs[0]?.orgId);
            headers["x-fv-orgid"] = orgsAndUserID.orgs[0];
            headers["x-fv-userid"] = orgsAndUserID.ID;
        }
    }
    return headers;
};

const getClientIP = async () => {
    try {
        const request = await api.get(`https://api.ipify.org/?format=json`);
        return request.data.ip;
    } catch (e) {
        console.log(e);
    }
    return "127.0.0.1";
};

const createQueryString = (params: { [key: string]: string }) =>
    Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

const base64UrlEncode = (str: string) =>
    str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

// Generate code_verifier
const generateCodeVerifier = () => {
    const codeVerifier = CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Base64);
    return base64UrlEncode(codeVerifier);
};

// Generate code_challenge
const generateCodeChallenge = async (codeVerifier: string) => {
    const hash = CryptoJS.SHA256(codeVerifier);
    return base64UrlEncode(hash.toString(CryptoJS.enc.Base64));
};

export const getUserOrgsAndUserID = async () => {
    const accessToken = await FVID.getFVIDAccessToken();
    if (!accessToken) {
        return null;
    }

    const environment: EnvironmentType = getEnvironmentSuffix();
    const request = await api.post(`${ENV_SETTNGS[environment].API_BASE || ""}/utils/GetUserOrgsWithToken`, null, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-fv-application': 'outlook-addin'
        }
    });

    return {
        orgs: request.data.orgs, ID: request.data.user.userId.native
    };
};

export default FVID;